/**
 * Create-report panel for reports.
 */
import * as React from 'react';
import { FileText, Smile, Star, AlertCircle } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Rating } from '@/components/forms/Rating';
import { Textarea } from '@/components/forms/Textarea';
import { Input } from '@/components/forms/Input';
import { createReport } from '@/services/inperson/reports.service';
import { getSession } from '@/services/inperson/sessions.service';
import { getSubjects } from '@/services/subjects.service';
import { useAuthStore } from '@/stores/authStore';
import type { CreateReportDto } from '@/types/inperson/report.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { reportValidationSchema } from '@/utils/validation-schemas-utils';
import {
  REPORT_IN_PERSON_QUERY_KEYS as QUERY_KEYS,
  REPORT_IN_PERSON_SUBJECTS_QUERY as SUBJECTS_QUERY,
  SESSION_REPORT_FEELING_OPTIONS,
  type ReportInPersonTab as Tab,
} from '@/components/reports/report-configs';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  buildCreateReportDto,
  createReportValidationPayload,
  emptyCreateReportForm,
  prefillSubjectIds,
  resolveReportSubjectOptions,
  useCreateReportFormHandlers,
} from '@/components/reports/inperson/report-form-utils';

/** Props for {@link CreateReportView}. */
interface CreateReportViewProps {
  reportType: Tab;
  sessionId?: string;
  onClose: () => void;
  onSuccess?: () => void;
  className?: string;
}

/** Create-report panel (session pick + type + form). */
export function CreateReportView({
  reportType,
  sessionId: initialSessionId,
  onClose,
  onSuccess,
  className,
}: Readonly<CreateReportViewProps>) {
  const user = useAuthStore((state) => state.user);
  const validation = useFormValidation(reportValidationSchema);
  const [formData, setFormData] = React.useState(emptyCreateReportForm);
  const subjectIdsTouchedByUser = React.useRef(false);
  const { handleFieldChange, handleSubjectIdsChange, handleBlur } = useCreateReportFormHandlers(
    setFormData,
    validation,
    subjectIdsTouchedByUser,
  );

  const { data: session } = useQuery({
    queryKey: QUERY_KEYS.session(initialSessionId ?? ''),
    queryFn: () => getSession(initialSessionId!),
    enabled: Boolean(initialSessionId),
  });

  const { data: allSubjects } = useQuery({
    queryKey: QUERY_KEYS.subjects,
    queryFn: () => getSubjects({ ...SUBJECTS_QUERY }),
    select: (response) => response.data,
  });

  const subjectOptions = React.useMemo(
    () => resolveReportSubjectOptions(session?.subjectIds, allSubjects ?? []),
    [session?.subjectIds, allSubjects],
  );

  React.useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      subjectIds: prefillSubjectIds(
        prev.subjectIds,
        session?.subjectIds,
        subjectIdsTouchedByUser.current,
      ),
    }));
  }, [session?.subjectIds]);

  const createMutation = useMutation({
    mutationFn: (dto: CreateReportDto) => createReport(initialSessionId!, dto),
    onSuccess: () => {
      toast.success('Compte rendu créé avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Validate, POST the report, then close. */
  const handleSubmit = () => {
    if (!initialSessionId) {
      toast.error(getErrorMessage('noSessionSelected'));
      return;
    }

    if (!user?.id) {
      toast.error(getErrorMessage('userNotSignedIn'));
      return;
    }

    const payload = createReportValidationPayload(formData);
    const isValid = validation.validateForm(payload);
    if (!isValid) {
      validation.markAsTouched('subject');
      validation.markAsTouched('rating');
      validation.markAsTouched('comment');
      toast.error(getErrorMessage('requiredReportFields'));
      return;
    }

    createMutation.mutate(buildCreateReportDto(user, formData));
  };

  const isInformationMeeting = reportType === 'information-meeting';

  const title = isInformationMeeting
    ? "Créer une synthèse de réunion d'information"
    : 'Créer un compte rendu de tutorat';

  const subtitle = isInformationMeeting
    ? "Renseignez les informations de la synthèse de réunion d'information"
    : 'Renseignez les informations du compte rendu de tutorat';

  return (
    <CollapseReplaceForm
      formType="create"
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={!initialSessionId ? false : validation.isFormValid()}
      className={className}
    >
      {!initialSessionId && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 flex gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="text-sm text-foreground">
            <p className="font-medium mb-1">{getErrorMessage('sessionRequiredTitle')}</p>
            <p className="text-muted-foreground">
              {getErrorMessage('sessionRequiredToCreateReport')}
            </p>
          </div>
        </div>
      )}

      {!initialSessionId && (
        <Input
          label="Séance"
          placeholder="Rechercher une séance..."
          inputSize="sm"
          helperText="Sélectionnez une séance sans CR existant"
          disabled
          className="mb-4"
        />
      )}

      {initialSessionId && (
        <>
          <div className="form-section">
            <h4 className="form-section-title">
              <Star className="w-4 h-4" />
              Évaluation
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Rating
                label="Note de la séance"
                required
                value={formData.rating}
                onChange={(value) => handleFieldChange('rating', value ?? null)}
                onBlur={() => handleBlur('rating')}
                state={validation.getFieldState('rating')}
                error={validation.getFieldError('rating')}
                helperText="Évaluez la qualité de la séance"
              />
              <MultiSelect
                label="Matières abordées"
                required
                placeholder="Sélectionner une ou plusieurs matières"
                options={subjectOptions}
                value={formData.subjectIds}
                onChange={handleSubjectIdsChange}
                onBlur={() => handleBlur('subject')}
                state={validation.getFieldState('subject')}
                error={validation.getFieldError('subject')}
                helperText="Matières abordées durant la séance"
                maxVisibleTags={3}
              />
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">
              <Smile className="w-4 h-4" />
              Comment vous êtes-vous senti pendant cette séance ?
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Optionnel — indiquez votre ressenti en tant que tuteur.
            </p>
            <div className="flex flex-col gap-2">
              {SESSION_REPORT_FEELING_OPTIONS.map(({ value, label }) => {
                const selected = formData.feeling === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleFieldChange('feeling', selected ? null : value)}
                    className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                      selected
                        ? 'border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)] font-medium text-foreground'
                        : 'border-border bg-background text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">
              <FileText className="w-4 h-4" />
              Commentaire
            </h4>
            <Textarea
              label="Commentaires et observations"
              required
              placeholder="Ex: Bonne participation, progrès notables"
              value={formData.comment}
              maxLength={1000}
              showCharCount
              onChange={(e) => handleFieldChange('comment', e.target.value)}
              onBlur={() => handleBlur('comment')}
              state={validation.getFieldState('comment')}
              error={validation.getFieldError('comment')}
              rows={4}
              autoResize
              helperText="Décrivez les observations et le déroulement de la séance"
            />
          </div>
        </>
      )}
    </CollapseReplaceForm>
  );
}
